import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { EntityModel } from '@data-layer/models/entities';
import { DESC_ENTITY } from '@constants/desc-entity';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { EntityValidationHelper } from '@helpers/entity-validation.helper';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { ActivatedRoute } from '@angular/router';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { CompaniesService } from '@services/companies.service';
import { takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import { GenericService } from '@services/generic.service';

@Component({
  selector: 'desktop-summary-entity',
  template: `
    <div
      class="summary-card-entity"
      [ngClass]="{
        'is-last': isLastInRow,
        'has-error': !entityIsValid,
        'is-editable': isEditMode,
        'd-flex flex-column': isAllEntityFieldsMissing
      }"
    >
      <div class="summary-card-entity-header">
        <div class="summary-card-entity-header-text">
          <h3>{{ (!!cardTitle ? cardTitle : entityInfo?.titleDesktop) | translate }}</h3>

          <span class="label-entity-required" *ngIf="isRequired && (!entityIsValid || !entity) && !isAnyOcrFlagEnabled">
            {{ 'LABELS.REQUIRED' | translate }}
          </span>

          <span class="label-entity-edited" *ngIf="isEditedRecently && isEditMode">{{ 'LABELS.EDITED' | translate }}</span>

          <span class="label-entity-optional" *ngIf="!isRequired && isOptional">{{ 'LABELS.OPTIONAL' | translate }}</span>
        </div>

        <div class="d-flex d-print-none ml-2">
          <button
            type="button"
            class="btn btn-link"
            [ngClass]="{ 'summary-card-entity__edit-button': isShowEncircledButton }"
            *ngIf="isEditMode && !isAllEntityFieldsMissing"
            (click)="edit.emit()"
          >
            <svg width="19" height="19" viewBox="0 0 19 19" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 14.2525V18.0025H3.75L14.81 6.9425L11.06 3.1925L0 14.2525ZM17.71 4.0425C18.1 3.6525 18.1 3.0225 17.71 2.6325L15.37 0.2925C14.98 -0.0975 14.35 -0.0975 13.96 0.2925L12.13 2.1225L15.88 5.8725L17.71 4.0425Z"
                fill="currentColor"
              />
            </svg>
          </button>

          <button type="button" class="btn btn-link ml-2" *ngIf="isCanRemove" (click)="remove.emit()">
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </div>

      <div class="summary-card-entity-body" [ngClass]="{ 'grow-centered': isAllEntityFieldsMissing }">
        <ng-container *ngIf="!!entity && !isAllEntityFieldsMissing">
          <ng-template *ngTemplateOutlet="entityItem; context: { entityItem: entity }"></ng-template>
        </ng-container>

        <div *ngIf="isAllEntityFieldsMissing && isEditMode" class="summary-card-entity-body__missing-fields">
          <div class="summary-card-entity-body__missing-fields__edit-button" (click)="edit.emit()">
            <img class="summary-card-entity-body__missing-fields__edit-button__icon" src="assets/icons/icon_edit_gray_md.svg" alt="edit" />
          </div>
        </div>

        <span class="entity-optional-hint" *ngIf="!entity && !!addHintText" [innerHTML]="addHintText | translate"></span>

        <span
          class="entity-required-hint error"
          *ngIf="isRequired && !entity && !canBeSubmitted && !isAnyOcrFlagEnabled"
          [innerHTML]="
            (isISF ? (isISFImporter ? 'LABELS.REQUIRED_ISF_ENTITY_IMPORTER' : 'LABELS.REQUIRED_ISF_ENTITY') : 'LABELS.REQUIRED_ES_ENTITY')
              | translate
          "
        ></span>

        <span
          class="entity-required-hint warning"
          *ngIf="isRequired && (!entity || isAllEntityFieldsMissing) && canBeSubmitted"
          [innerHTML]="'LABELS.REQUIRED_ISF_ENTITY_CAN_SUBMIT' | translate"
        ></span>
      </div>
    </div>

    <ng-template #entityItem let-entityItem="entityItem" let-index="index">
      <div class="entity-value-row" *ngIf="isShowIdentifier">
        <div class="entity-value-key">{{ 'LABELS.ID_NUMBER' | translate }}</div>
        <ng-container *ngIf="!getValidationText('entityIdentifier'); else missedIdentifier">
          <div class="entity-value">
            <span *ngIf="entityItem?.entityIdentifierQualifier !== null">
              {{ 'IDENTIFIER_QUALIFIER.' + entityItem.entityIdentifierQualifier | translate }}
            </span>
            <span *ngIf="entityItem?.entityIdentifier">{{ entityItem.entityIdentifier | identifier }}</span>
          </div>
        </ng-container>
        <ng-template #missedIdentifier>
          <div class="entity-value" *ngIf="isAnyOcrFlagEnabled">-----</div>

          <div *ngIf="!isAnyOcrFlagEnabled" class="entity-value text-error">
            <span class="w-100 d-inline-block" *ngIf="getValidationText('entityIdentifier')">
              {{ getValidationText('entityIdentifier') | translate }}
            </span>
            <span class="w-100 d-inline-block" *ngIf="getValidationText('entityIdentifierQualifier')">
              {{ getValidationText('entityIdentifierQualifier') | translate }}
            </span>
          </div>
        </ng-template>
      </div>

      <div
        class="entity-value-row"
        *ngIf="entityItem?.dateOfBirth || entityItem?.entityIdentifierQualifier === enumIdentifierQualifier.SSN"
      >
        <div class="entity-value-key">{{ 'FORM_LABEL.DATEOFBIRTH' | translate }}</div>

        <ng-container *ngIf="!getValidationText('dateOfBirth'); else missedDateOfBirth">
          <div class="entity-value">{{ entityItem.dateOfBirth | customDateByUtc }}</div>
        </ng-container>
        <ng-template #missedDateOfBirth>
          <div class="entity-value text-error">{{ getValidationText('dateOfBirth') | translate }}</div>
        </ng-template>
      </div>

      <div class="entity-value-row">
        <div class="entity-value-key">{{ 'LABELS.NAME' | translate }}</div>

        <ng-container *ngIf="!getValidationText('name'); else missedName">
          <div class="entity-value">{{ entityItem?.name }}</div>
        </ng-container>
        <ng-template #missedName>
          <div class="entity-value" [ngClass]="{ 'text-error': !isAnyOcrFlagEnabled }">
            {{ isAnyOcrFlagEnabled ? '-----' : (getValidationText('name') | translate) }}
          </div>
        </ng-template>
      </div>

      <div class="entity-value-row" *ngIf="isShowPhone">
        <div class="entity-value-key">{{ 'LABELS.MOBILE_NUMBER' | translate }}</div>

        <ng-container *ngIf="!getValidationText('phone'); else missedPhone">
          <div class="entity-value">{{ entityItem?.phone }}</div>
        </ng-container>
        <ng-template #missedPhone>
          <div class="entity-value text-error">{{ getValidationText('phone') | translate }}</div>
        </ng-template>
      </div>

      <div class="entity-value-row" *ngIf="isShowEmail">
        <div class="entity-value-key">{{ 'LABELS.EMAIL_ADDRESS' | translate }}</div>

        <ng-container *ngIf="!getValidationText('email'); else missedEmail">
          <div class="entity-value entity-value-wrap">{{ entityItem?.email }}</div>
        </ng-container>
        <ng-template #missedEmail>
          <div class="entity-value text-error">{{ getValidationText('email') | translate }}</div>
        </ng-template>
      </div>

      <div class="entity-value-row">
        <div class="entity-value-key">{{ 'LABELS.ADDRESS' | translate }}</div>

        <ng-container *ngIf="isValidEntityAddress(); else missedAdd">
          <div class="entity-value" *ngIf="!getValidationText('address')">
            <address-details [address]="entityItem?.address"></address-details>
          </div>
          <div class="entity-value" [ngClass]="{ 'text-error': !isAnyOcrFlagEnabled }" *ngIf="getValidationText('address')">
            {{ isAnyOcrFlagEnabled ? '-----' : (getValidationText('address') | translate) }}
          </div>
        </ng-container>
        <ng-template #missedAdd>
          <div class="entity-value" [ngClass]="{ 'text-error': !isAnyOcrFlagEnabled }">
            {{ isAnyOcrFlagEnabled ? '-----' : ('BACK_ERROR.MISSING_ADDRESS_PARTS' | translate) }}
          </div>
        </ng-template>
      </div>

      <div class="entity-value-row stick-to-bottom" *ngIf="!!sameAs">
        <div class="entity-is-same-as">
          <i>=</i>
          {{ sameAs }}
        </div>
      </div>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./desktop-summary-entity.component.scss'],
})
export class DesktopSummaryEntityComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public entity: EntityModel;
  @Input() public entityCode: enumEntityModelCode = enumEntityModelCode.IMPORTER;
  @Input() public cardTitle: string;
  @Input() public isEditMode: boolean;
  @Input() public isShowIdentifier: boolean = true;
  @Input() public isShowEncircledButton: boolean = false;
  @Input() public isShowPhone: boolean;
  @Input() public isShowEmail: boolean;
  @Input() public isRequired: boolean = true;
  @Input() public isOptional: boolean = true;
  @Input() public isSkipValidation: boolean;
  @Input() public isEditedRecently: boolean;
  @Input() public isLastInRow: boolean;
  @Input() public isCanRemove: boolean;
  @Input() public canBeSubmitted: boolean;
  @Input() public sameAs: string;
  @Input() public addHintText: string;
  @Input() public validationKeys: string[] = [];
  @Output() public edit: EventEmitter<void> = new EventEmitter<void>();
  @Output() public remove: EventEmitter<void> = new EventEmitter<void>();

  public entityInfo: {
    CODE: enumEntityModelCode;
    imgSrc: string;
    titleDesktop: string;
    subTitle: string;
    subTitleDesktop: string;
    hint: string;
    addDetails: string;
    tooltip: string;
    addNew: string;
    subTitleHint: string;
  } = null;
  public entityIsValid: boolean;

  public enumIdentifierQualifier = enumIdentifierQualifier;
  public enumEntityModelCode = enumEntityModelCode;
  public isISF: boolean;

  private isMobile: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private activeRoute: ActivatedRoute,
    private genericService: GenericService,
    private companiesService: CompaniesService,
  ) {}

  public ngOnInit(): void {
    this.isISF = this.activeRoute?.parent?.snapshot?.routeConfig?.path === 'isf' || location.pathname.includes('isf/batch');
    this.entityInfo = DESC_ENTITY[this.entityCode];
    this.getIsValid();

    this.genericService.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile = isMobile;
      this.cdr.detectChanges();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.entity) {
      this.getIsValid();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isValidEntityAddress(): boolean {
    if (this.isSkipValidation) {
      return true;
    }

    const missingAddressKeys = ['country', 'city', 'line1'];
    const isFilledAddress = missingAddressKeys.every((item) => this.entity?.address?.[item]);

    return EntityValidationHelper.isValidEntityAddress(this.entity?.address) && isFilledAddress;
  }

  public getValidationText(fieldName: string): string {
    if (this.isSkipValidation) {
      return null;
    }

    return (
      EntityValidationHelper.getEntityValidationText(this.entity, fieldName) ||
      (this.validationKeys.includes(fieldName) && !this.entity[fieldName] ? 'BACK_ERROR.MISSING' : null)
    );
  }

  public get isISFImporter(): boolean {
    return this.isISF && this.entityCode === enumEntityModelCode.IMPORTER;
  }

  public get isAllEntityFieldsMissing(): boolean {
    switch (this.entityCode) {
      case enumEntityModelCode.IMPORTER:
      case enumEntityModelCode.CONSIGNEE:
      case enumEntityModelCode.BUYER:
        return (
          this.isAnyOcrFlagEnabled &&
          (((this.isShowIdentifier ? !!this.getValidationText('entityIdentifier') : true) &&
            !!this.getValidationText('name') &&
            (!this.isValidEntityAddress() || (this.isValidEntityAddress() ? !!this.getValidationText('address') : true))) ||
            !this.entity)
        );
      case enumEntityModelCode.SELLER:
      case enumEntityModelCode.SHIP_TO:
      case enumEntityModelCode.CONSOLIDATOR:
      case enumEntityModelCode.STAFFING_LOCATION:
        return (
          this.isAnyOcrFlagEnabled &&
          ((!!this.getValidationText('name') &&
            (!this.isValidEntityAddress() || (this.isValidEntityAddress() ? !!this.getValidationText('address') : true))) ||
            !this.entity)
        );
      default:
        return false;
    }
  }

  public get isAnyOcrFlagEnabled(): boolean {
    return this.isHasEsOcrRedesign || this.isHasIsfOcr;
  }

  public get isHasEsOcrRedesign(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.esOcrRedesign);
  }

  public get isHasIsfOcr(): boolean {
    const isWithUserWithIsfOcrFlag = this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.isfOcr);

    return isWithUserWithIsfOcrFlag && !this.isMobile;
  }

  private getIsValid(): void {
    if (this.isRequired) {
      this.entityIsValid =
        (!!this.entity &&
          (this.entity?.validationMessages?.length === 0 || !this.entity?.validationMessages) &&
          this.isValidEntityAddress()) ||
        this.isSkipValidation;
    } else {
      this.entityIsValid = !!this.entity
        ? ((this.entity?.validationMessages?.length === 0 || !this.entity?.validationMessages) && this.isValidEntityAddress()) ||
          this.isSkipValidation
        : true;
    }
  }
}
